import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import AcademyHeader from '../../components/AcademyHeader';
import AcademyFooter from '../../components/AcademyFooter';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // 60s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [tx, setTx] = useState(null);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setError('Missing session id'); return; }
    let active = true;

    const poll = async () => {
      polls.current += 1;
      try {
        const { data } = await axios.get(`${API}/api/academy/checkout/status/${sessionId}`);
        if (!active) return;
        setTx(data);
        if (data.payment_status === 'paid') { setPaid(true); return; }
        if (data.status === 'expired') { setError('This payment link has expired.'); return; }
        if (polls.current >= MAX_POLLS) { setError('Timed out waiting for payment confirmation.'); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.detail || err.message);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white">
      <AcademyHeader />
      <main className="max-w-[760px] mx-auto px-4 lg:px-6 py-16 text-center">
        {error ? (
          <>
            <h1 className="text-[28px] font-extrabold text-red-600 mb-2">Payment problem</h1>
            <p className="text-[15px] text-[#6E6E73] mb-6">{error}</p>
            <Link to="/academy/enroll" className="inline-flex px-6 py-3 rounded-full bg-[#c9a227] text-white font-bold">Try again</Link>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-[34px] font-extrabold text-[#1D1D1F] mb-2">Payment confirmed</h1>
            <p className="text-[15px] text-[#6E6E73] mb-2">
              Thanks{tx?.metadata?.name ? `, ${tx.metadata.name}` : ''}! You're enrolled in <strong>{tx?.service_name}</strong>{' '}
              ({tx?.tier_name}).
            </p>
            <p className="text-[14px] text-[#6E6E73] mb-8">
              We've created your ZHELTA Academy account and emailed your temporary password to{' '}
              <strong>{tx?.metadata?.email}</strong>. You can log in below to access your portal.
            </p>
            <Link
              to="/academy/login"
              data-testid="payment-success-login"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-[14px] font-bold uppercase tracking-[0.18em] text-[#0e0e10]"
              style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227,#a8871a)' }}
            >
              Sign in to my portal
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-[#c9a227] mx-auto mb-4 animate-spin" />
            <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-2">Confirming your payment…</h1>
            <p className="text-[15px] text-[#6E6E73]">Hang tight — this only takes a few seconds.</p>
          </>
        )}
      </main>
      <AcademyFooter />
    </div>
  );
}
